/**
 * Where an investment applies: Worldwide, a country, a state, or a region the
 * owner names and builds from countries and states chosen one by one.
 *
 * Worldwide means every location of this business, never other businesses.
 * Codes are the same stable country and state codes locations carry, so a
 * scope keeps meaning the same thing when a location opens or closes.
 */

import { useState, type ReactNode } from 'react';
import { X } from 'lucide-react';
import {
  COUNTRIES,
  INVESTMENT_SCOPE_KINDS,
  INVESTMENT_SCOPE_LABELS,
  findCountry,
  findState,
  regionTermOf,
  statesOf,
  type InvestmentScopeInput,
} from '@cenox/shared';
import { Button, Input, Select, cn } from '../../components/ui';

const HINTS: Record<InvestmentScopeInput['kind'], string> = {
  worldwide: 'Every location of this business, wherever it is.',
  country: 'Locations in one country.',
  state: 'Locations in one state or province of a country.',
  region: 'A region you name, made of the countries and states you choose.',
};

export function areaText(country: string, state?: string | null): string {
  const c = findCountry(country)?.name ?? country;
  return state ? `${c} → ${findState(country, state)?.name ?? state}` : c;
}

export function ScopeSelector({
  value,
  onChange,
  defaultCountry,
  errors,
  legend = 'Geographical scope',
}: {
  value: InvestmentScopeInput;
  onChange: (next: InvestmentScopeInput) => void;
  defaultCountry: string;
  errors?: Record<string, string[] | undefined>;
  legend?: string;
}): ReactNode {
  const [addCountry, setAddCountry] = useState(defaultCountry);
  const [addState, setAddState] = useState('');
  const countryOptions = COUNTRIES.map((c) => ({ value: c.code, label: c.name }));
  const country = value.country ?? defaultCountry;
  const states = statesOf(country);
  const term = regionTermOf(country);
  const areas = value.areas ?? [];

  const setKind = (kind: InvestmentScopeInput['kind']) => {
    if (kind === 'worldwide') onChange({ kind });
    else if (kind === 'country') onChange({ kind, country });
    else if (kind === 'state') onChange({ kind, country, state: statesOf(country)[0]?.code ?? '' });
    else onChange({ kind, regionName: value.regionName ?? '', areas });
  };

  return (
    <fieldset className="space-y-3">
      <legend className="label-base">{legend}</legend>
      <div role="radiogroup" aria-label={legend} className="grid grid-cols-2 gap-1 rounded-lg bg-slate-100 p-1 sm:grid-cols-4">
        {INVESTMENT_SCOPE_KINDS.map((k) => (
          <button
            key={k}
            type="button"
            role="radio"
            aria-checked={value.kind === k}
            onClick={() => setKind(k)}
            className={cn(
              'rounded-md px-2 py-1.5 text-sm font-medium transition-colors',
              value.kind === k ? 'bg-white text-violet-700 shadow-sm ring-1 ring-violet-200' : 'text-slate-600 hover:text-slate-900',
            )}
          >
            {INVESTMENT_SCOPE_LABELS[k]}
          </button>
        ))}
      </div>
      <p className="text-xs text-slate-500">{HINTS[value.kind]}</p>

      {value.kind === 'country' || value.kind === 'state' ? (
        <div className="grid gap-3 sm:grid-cols-2">
          <Select
            label="Country"
            value={country}
            options={countryOptions}
            onChange={(e) =>
              onChange(
                value.kind === 'state'
                  ? { kind: 'state', country: e.target.value, state: statesOf(e.target.value)[0]?.code ?? '' }
                  : { kind: 'country', country: e.target.value },
              )
            }
            error={errors?.['scope.country']?.[0] ?? errors?.country?.[0]}
          />
          {value.kind === 'state' ? (
            states.length ? (
              <Select
                label={term.singular}
                value={value.state ?? ''}
                options={states.map((s) => ({ value: s.code, label: s.name }))}
                onChange={(e) => onChange({ kind: 'state', country, state: e.target.value })}
                error={errors?.['scope.state']?.[0] ?? errors?.state?.[0]}
              />
            ) : (
              <p className="self-end text-xs text-amber-700">
                {findCountry(country)?.name ?? country} has no {term.plural.toLowerCase()} listed. Choose Country instead.
              </p>
            )
          ) : null}
        </div>
      ) : null}

      {value.kind === 'region' ? (
        <div className="space-y-3">
          <Input
            label="Region name"
            value={value.regionName ?? ''}
            placeholder="e.g. South-East"
            onChange={(e) => onChange({ ...value, kind: 'region', regionName: e.target.value })}
            error={errors?.['scope.regionName']?.[0] ?? errors?.regionName?.[0]}
          />
          <div>
            <p className="label-base">Includes</p>
            {areas.length === 0 ? (
              <p className="text-xs text-slate-500">Nothing yet. Add the countries or states this region covers.</p>
            ) : (
              <ul className="flex flex-wrap gap-1.5" aria-label="Region includes">
                {areas.map((a) => (
                  <li
                    key={`${a.country}:${a.state ?? ''}`}
                    className="inline-flex items-center gap-1 rounded-full bg-violet-50 px-2.5 py-1 text-xs font-medium text-violet-800 ring-1 ring-violet-200"
                  >
                    {areaText(a.country, a.state)}
                    <button
                      type="button"
                      aria-label={`Remove ${areaText(a.country, a.state)}`}
                      className="rounded-full p-0.5 hover:bg-violet-100"
                      onClick={() => onChange({ ...value, kind: 'region', areas: areas.filter((x) => x !== a) })}
                    >
                      <X className="size-3" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {errors?.['scope.areas']?.[0] ?? errors?.areas?.[0] ? (
              <p role="alert" className="mt-1 text-xs font-medium text-red-600">
                {errors?.['scope.areas']?.[0] ?? errors?.areas?.[0]}
              </p>
            ) : null}
          </div>
          <div className="grid items-end gap-2 sm:grid-cols-[1fr_1fr_auto]">
            <Select
              label="Country"
              value={addCountry}
              options={countryOptions}
              onChange={(e) => {
                setAddCountry(e.target.value);
                setAddState('');
              }}
            />
            <Select
              label={regionTermOf(addCountry).singular}
              value={addState}
              placeholder="Whole country"
              options={statesOf(addCountry).map((s) => ({ value: s.code, label: s.name }))}
              onChange={(e) => setAddState(e.target.value)}
              disabled={statesOf(addCountry).length === 0}
            />
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                const next = { country: addCountry, state: addState || null };
                if (areas.some((a) => a.country === next.country && (a.state ?? null) === next.state)) return;
                onChange({ ...value, kind: 'region', areas: [...areas, next] });
                setAddState('');
              }}
            >
              Add to region
            </Button>
          </div>
        </div>
      ) : null}
    </fieldset>
  );
}
